/**
 * Bundle add to cart, straight from the product card.
 *
 * A "Bundle" tagged product card's Add to bag adds that bundle to the cart on the
 * spot. There is nothing for the shopper to choose — a Rebuy dynamic bundle is a
 * fixed set of components, with no variant and no quantity to pick — so no quick
 * shop dialog is opened. Instead the bundle's own Rebuy widget is mounted off
 * screen, its trigger shell unchecked, and its own Add to cart clicked.
 *
 * Fetching the widget from the product's own URL means the correct widget id per
 * bundle (305297 / 306036 / 306129) comes along automatically — no new template,
 * no hardcoded ids.
 *
 * Only Rebuy component line items ever reach the cart. The bundle product itself
 * is a Rebuy trigger shell that cannot be fulfilled, so nothing here ever submits
 * a theme product form, and the shell's row in the widget is unchecked — and
 * *confirmed* unchecked — before the add is clicked, exactly as the PDP proxy
 * (assets/rebuy-bundle-atc-proxy.js) does.
 *
 * Failure behaviour: the CTA is a real <a href="{{ product.url }}">. Rebuy
 * missing, the fetch failing, the widget never rendering, its add button missing,
 * or the trigger shell refusing to come unchecked all fall through to that link,
 * landing the shopper on the bundle's own PDP where the widget is the page's own.
 * It never falls back to the theme's add-to-cart, which would put the
 * unfulfillable shell in the cart.
 *
 * Debugging: add ?bundle-quick-shop-debug to the URL (or set
 * window.BundleQuickShopDebug = true) to log each step.
 */
(function () {
  'use strict';

  var TRIGGER = '[data-bundle-quick-shop]';

  /* Apps section key shared by templates/product.rebuy-bundle-{one,two,three}.json.
     Only an optimisation: it turns the lookup into a small Section Rendering API
     request instead of a full PDP fetch. If the key ever changes (re-adding the
     app block in the theme editor mints a new one), the full-page fallback below
     still finds the widget. */
  var APPS_SECTION_ID = '1786386929bf96308f';

  var REBUY_WIDGET = '[data-rebuy-id], [data-rebuy-widget-id], [data-rebuy-component]';
  /* Rebuy's own cart/flyout chrome matches those selectors — never take it. */
  var REBUY_CART =
    '.rebuy-cart, .rebuy-cart__flyout, .rebuy-modal, rebuy-cart, [data-rebuy-component="cart"]';
  /* Rebuy renders one of these once a bundle is actually available. */
  var BUNDLE_CONTENT =
    '.rebuy-product-block, .rebuy-product-grid, .rebuy-bundle, .rebuy-widget__content';
  /* Rebuy's own Add to cart inside the bundle — the button the card's Add to bag
     is proxied to. Same selector the PDP proxy uses. */
  var REBUY_ATC =
    '.rebuy-widget.widget-type-dynamic-bundle .rebuy-bundle__actions-buttons .rebuy-button, ' +
    '.rebuy-bundle__actions-buttons .rebuy-button, ' +
    '.rebuy-button';
  /* The trigger shell's own row inside the widget. */
  var INPUT_ROW = '.rebuy-product-block.is-input-product';
  var INPUT_CHECKBOX = INPUT_ROW + ' .rebuy-checkbox, ' + INPUT_ROW + ' input[type="checkbox"]';
  /* Rebuy's post-add signals. */
  var CART_EVENTS = ['rebuy:cart.add', 'rebuy:cart.change', 'rebuy:smartcart.show'];

  var RENDER_TIMEOUT = 8000;
  /* Rebuy re-applies its defaults shortly after mount — keep unchecking for this long. */
  var UNCHECK_SETTLE = 2500;
  /* How long the shell gets to actually come off before the add is abandoned.
     Comfortably longer than keepShellUnchecked's three tries at 300ms apart, with
     room for Rebuy to re-render in between. */
  var UNCHECK_TIMEOUT = 3000;
  /* And how long it then has to *stay* off before the add is clicked. Rebuy
     re-applies its defaults a beat after the click that unchecked the shell, so
     reading "unchecked" once proves nothing — that read can land in the gap. */
  var SHELL_QUIET = 400;
  /* Rebuy's add is fire-and-forget: on some builds it adds without emitting any
     of CART_EVENTS. Stop spinning after this and let a cart refresh speak for it
     rather than leaving the button busy for ever. */
  var CART_TIMEOUT = 10000;

  var DEBUG =
    window.BundleQuickShopDebug === true ||
    window.location.search.indexOf('bundle-quick-shop-debug') !== -1;

  function log() {
    if (!DEBUG) return;
    console.log.apply(
      console,
      ['[bundle-quick-shop]'].concat(Array.prototype.slice.call(arguments))
    );
  }

  /* ---------------------------------------------------------------- fetch */

  var cache = {};

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function widgetIn(doc) {
    var nodes = doc.querySelectorAll(REBUY_WIDGET);
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.closest(REBUY_CART)) continue;
      /* Prefer the app block wrapper: it carries the block's own markup and scripts. */
      return node.closest('.shopify-block') || node;
    }
    return null;
  }

  function text(response) {
    if (!response.ok) throw new Error('HTTP ' + response.status);
    return response.text();
  }

  function fetchWidget(url) {
    if (cache[url]) return cache[url];

    var request = fetch(url + '?section_id=' + APPS_SECTION_ID)
      .then(text)
      .then(function (html) {
        var el = widgetIn(parse(html));
        if (!el) throw new Error('no widget in the apps section');
        log('widget from section rendering', url);
        return el;
      })
      .catch(function (error) {
        log('section fetch unusable, falling back to the full PDP —', error.message);
        return fetch(url)
          .then(text)
          .then(function (html) {
            var el = widgetIn(parse(html));
            if (!el) throw new Error('no Rebuy widget on ' + url);
            log('widget from full PDP', url);
            return el;
          });
      })
      .then(function (el) {
        return el.outerHTML;
      })
      .catch(function (error) {
        delete cache[url]; /* never cache a failure — the next click retries */
        throw error;
      });

    cache[url] = request;
    return request;
  }

  /* ---------------------------------------------------------------- mount */

  /* Injected <script> nodes are inert until recreated. */
  function runScripts(scope) {
    var scripts = scope.querySelectorAll('script');
    for (var i = 0; i < scripts.length; i++) {
      var old = scripts[i];
      var fresh = document.createElement('script');
      for (var a = 0; a < old.attributes.length; a++) {
        fresh.setAttribute(old.attributes[a].name, old.attributes[a].value);
      }
      fresh.appendChild(document.createTextNode(old.innerHTML));
      old.parentNode.replaceChild(fresh, old);
    }
  }

  /* Rebuy scans for widgets on page load; markup added later may need a nudge.
     Every entry point here is feature-detected — the API differs between Rebuy
     builds, and on some it re-scans on its own and none of these exist. */
  function nudgeRebuy(scope) {
    var rebuy = window.Rebuy;
    if (!rebuy) {
      log('window.Rebuy is not present');
      return false;
    }
    try {
      if (rebuy.Widgets && typeof rebuy.Widgets.init === 'function') rebuy.Widgets.init(scope);
      else if (typeof rebuy.init === 'function') rebuy.init();
      else if (rebuy.Loader && typeof rebuy.Loader.init === 'function') rebuy.Loader.init();
      else log('no known Rebuy init entry point — relying on its own scan');
    } catch (error) {
      log('Rebuy init threw', error);
    }
    return true;
  }

  function hasBundle(mount) {
    if (mount.querySelector(BUNDLE_CONTENT)) return true;
    /* Class names vary by widget type — fall back to "Rebuy rendered something real". */
    return !!mount.querySelector('[class*="rebuy-"]') && mount.textContent.trim() !== '';
  }

  function waitForRender(mount) {
    return new Promise(function (resolve, reject) {
      if (hasBundle(mount)) return resolve();

      var timer = null;
      var observer = new MutationObserver(function () {
        if (!hasBundle(mount)) return;
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      });
      observer.observe(mount, { childList: true, subtree: true });

      timer = setTimeout(function () {
        observer.disconnect();
        reject(new Error('Rebuy did not render within ' + RENDER_TIMEOUT + 'ms'));
      }, RENDER_TIMEOUT);
    });
  }

  /* -------------------------------------------------------- trigger shell */

  /* `checked` is authoritative when the control is a real checkbox — reading the
     row's `is-selected` class as well would keep reporting "selected" after the
     click, and clicking again would toggle the shell back on. Rebuy styles some
     controls as non-inputs, so the class stays as the fallback for those. */
  function shellSelected(mount) {
    var checkbox = mount.querySelector(INPUT_CHECKBOX);
    if (!checkbox) return false;
    if (typeof checkbox.checked === 'boolean') return checkbox.checked;
    return !!checkbox.closest(INPUT_ROW + '.is-selected');
  }

  /**
   * The bundle product is a Rebuy trigger shell, not something that can be
   * fulfilled, so its row must never be checked. Click the control rather than
   * setting `checked`, so Rebuy's own handlers update selection and price, then
   * hold it off while Rebuy re-applies its defaults.
   */
  function keepShellUnchecked(mount) {
    var settleUntil = 0;
    var clicks = 0;
    var lastClick = 0;

    function uncheck() {
      var checkbox = mount.querySelector(INPUT_CHECKBOX);
      if (!checkbox) return false;
      if (shellSelected(mount)) {
        var now = Date.now();
        /* Never hammer the control: a click that does not take is a Rebuy state
           we do not understand, and retrying it risks selecting the shell. */
        if (clicks < 3 && now - lastClick > 300) {
          log('unchecking the trigger shell');
          clicks++;
          lastClick = now;
          checkbox.click();
        }
      }
      if (!settleUntil) settleUntil = Date.now() + UNCHECK_SETTLE;
      return true;
    }

    uncheck();

    var observer = new MutationObserver(function () {
      if (!settleUntil) {
        uncheck();
        return;
      }
      if (Date.now() > settleUntil) return observer.disconnect();
      if (shellSelected(mount)) uncheck();
    });
    observer.observe(mount, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'checked'],
    });

    var stop = setTimeout(function () {
      observer.disconnect();
    }, RENDER_TIMEOUT + UNCHECK_SETTLE);

    return function () {
      clearTimeout(stop);
      observer.disconnect();
    };
  }

  /**
   * Wait until the shell has come off and stayed off for SHELL_QUIET.
   *
   * The step the modal never needed: there, the shopper's own click landed
   * seconds after the widget mounted, long after Rebuy had settled. Here the add
   * is clicked as soon as the widget renders, so "unchecked" has to be observed
   * rather than assumed — clicking add while the shell is selected would put the
   * shell in the cart.
   *
   * A single "it reads unchecked" is not enough, which is the whole reason for the
   * quiet window: `keepShellUnchecked` has only just clicked the control, and
   * Rebuy re-applies its defaults a beat later, so the obvious check resolves
   * inside the gap and the add goes out while Rebuy is putting the shell back.
   *
   * Polled rather than observed on purpose: `.click()` on a real checkbox flips
   * the `checked` *property*, which mutates no attribute and fires no observer.
   */
  function waitForShellUnchecked(mount) {
    return new Promise(function (resolve, reject) {
      var deadline = Date.now() + UNCHECK_TIMEOUT;
      var quietSince = 0;

      (function poll() {
        if (shellSelected(mount)) {
          quietSince = 0;
          if (Date.now() > deadline) return reject(new Error('the trigger shell stayed selected'));
          return setTimeout(poll, 100);
        }
        if (!quietSince) quietSince = Date.now();
        if (Date.now() - quietSince >= SHELL_QUIET) return resolve();
        if (Date.now() > deadline) {
          return reject(new Error('the trigger shell would not stay unselected'));
        }
        setTimeout(poll, 100);
      })();
    });
  }

  /* On a bundle PDP, rebuy-bundle-atc-proxy.js hides widgets matching that
     section's selector with an inline `display: none !important`. If it catches
     the copy mounted here, undo it — a widget that is not laid out never builds.
     Deliberately narrow: the app block wrapper and the widget host only. Rebuy's
     own inner nodes use inline display legitimately and must be left alone. */
  function unhide(mount) {
    mount.style.removeProperty('display');
    var nodes = mount.querySelectorAll('.shopify-block, ' + REBUY_WIDGET);
    for (var i = 0; i < nodes.length; i++) nodes[i].style.removeProperty('display');
  }

  function rebuyAddButton(mount) {
    var buttons = mount.querySelectorAll(REBUY_ATC);
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      if (!button || button.disabled) continue;
      /* Skip Rebuy cart / flyout chrome if it ever shares the class. */
      if (button.closest(REBUY_CART)) continue;
      return button;
    }
    return null;
  }

  /* ------------------------------------------------------------- card UI */

  /* Where the widget goes: laid out — a lazily-rendered Rebuy widget has to be in
     the layout tree to keep building, and some builds only start once they are in
     view — but invisible and untouchable. See `.bundle-atc__host` in
     assets/custom.css; never `display: none`. */
  function makeHost() {
    var host = document.createElement('div');
    host.className = 'bundle-atc__host';
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
    return host;
  }

  var announcer = null;

  /* One shared live region: the card button shows a spinner, which on its own
     says nothing to a screen reader. */
  function announce(message) {
    if (!message && !announcer) return;
    if (!announcer) {
      announcer = document.createElement('p');
      announcer.className = 'visually-hidden';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      document.body.appendChild(announcer);
    }
    announcer.textContent = message || '';
  }

  function setBusy(trigger, busy) {
    trigger.classList.toggle('bundle-atc--busy', busy);
    if (busy) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.setAttribute('aria-disabled', 'true');
    } else {
      trigger.removeAttribute('aria-busy');
      trigger.removeAttribute('aria-disabled');
    }
  }

  function openCart(opener) {
    if (document.body.getAttribute('data-cart-type') !== 'drawer') return;
    var drawer = window.shtCartDrawer || document.querySelector('sht-cart-drwr');
    if (!drawer || typeof drawer.openDrawer !== 'function') return;
    drawer.openDrawer(document.querySelector('.js-header-cart-status-btn') || opener);
  }

  function refreshCart() {
    document.dispatchEvent(
      new CustomEvent('cart:update', { detail: { source: 'bundle-quick-shop' } })
    );
  }

  /* ----------------------------------------------------------------- add */

  var session = 0;
  var teardown = null;

  /**
   * Add the bundle behind `trigger` to the cart. Returns false when the click
   * cannot be taken over, so the caller leaves it as a plain link to the PDP.
   */
  function add(trigger) {
    var url = (trigger.getAttribute('data-bundle-url') || trigger.getAttribute('href') || '').split(
      '?'
    )[0];
    if (!url) return false;

    /* A click already in flight on this card: swallow this one rather than
       starting the whole thing over. */
    if (trigger.getAttribute('aria-busy') === 'true') return true;

    /* One add at a time — a second card cancels the first, restoring its button
       and taking its widget back out of the page. */
    if (teardown) teardown();

    var token = ++session;
    var mount = makeHost();
    var stopUnchecking = null;
    var giveUp = null;
    var settled = false;

    setBusy(trigger, true);
    announce(trigger.getAttribute('data-bundle-loading') || '');
    log('adding', url);

    function cleanup() {
      CART_EVENTS.forEach(function (name) {
        document.removeEventListener(name, onCartEvent);
      });
      clearTimeout(giveUp);
      if (stopUnchecking) stopUnchecking();
      setBusy(trigger, false);
      announce('');
      /* Given a beat before removal: Rebuy does its own post-add work in the
         widget it just used, and yanking it out mid-flight is a needless risk. */
      setTimeout(function () {
        if (mount.parentNode) mount.parentNode.removeChild(mount);
      }, 1000);
      teardown = null;
    }

    function onCartEvent(event) {
      if (token !== session || settled) return;
      settled = true;
      log('cart event', event.type);
      cleanup();
      /* Let the theme's cart drawer and count re-read the cart after Rebuy's add. */
      refreshCart();
      /* Rebuy's own Smart Cart is already showing the result — don't stack ours on top. */
      if (event.type !== 'rebuy:smartcart.show') openCart(trigger);
    }

    /* Nothing was added, and nothing may be: the shell is the only thing this
       page could add on its own and it cannot be fulfilled. Fall through to the
       CTA's own href, where the bundle is the page's own widget. */
    function bail(error) {
      if (token !== session || settled) return;
      settled = true;
      log('cannot add here —', (error && error.message) || error);
      cleanup();
      window.location.href = url;
    }

    teardown = cleanup;

    fetchWidget(url)
      .then(function (html) {
        if (token !== session) return false;
        mount.innerHTML = html;
        runScripts(mount);
        unhide(mount);
        if (!nudgeRebuy(mount)) throw new Error('Rebuy is not loaded on this page');
        return waitForRender(mount).then(function () {
          return true;
        });
      })
      .then(function (rendered) {
        if (!rendered || token !== session) return false;
        log('widget rendered');
        stopUnchecking = keepShellUnchecked(mount);
        return waitForShellUnchecked(mount).then(function () {
          return true;
        });
      })
      .then(function (ready) {
        if (!ready || token !== session) return;

        var button = rebuyAddButton(mount);
        if (!button) throw new Error('Rebuy has no add button');
        /* Belt and braces: the poll resolved, but read the truth again at the
           last possible moment. The shell must not be in what this click adds. */
        if (shellSelected(mount)) throw new Error('the trigger shell is still selected');

        /* Armed here, not at the start: Rebuy's cart events are global, and one
           it emits while its own widget is initialising would otherwise be read
           as this add having landed. From this line on, the next one is ours. */
        CART_EVENTS.forEach(function (name) {
          document.addEventListener(name, onCartEvent);
        });

        log('clicking the Rebuy add to cart');
        button.click();

        giveUp = setTimeout(function () {
          if (token !== session || settled) return;
          settled = true;
          log('no cart event within ' + CART_TIMEOUT + 'ms — refreshing the cart anyway');
          cleanup();
          /* The add may well have landed, so refresh the count rather than
             guessing — but leave the shopper where they are instead of opening a
             drawer that might have nothing new in it. */
          refreshCart();
        }, CART_TIMEOUT);
      })
      .catch(bail);

    return true;
  }

  /* Delegated, so cards rendered later (filters, infinite scroll) work too. */
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var target = event.target;
    if (!target || !target.closest) return;
    var trigger = target.closest(TRIGGER);
    if (!trigger) return;
    /* Anything that cannot be taken over stays a plain link to the PDP. */
    if (add(trigger)) event.preventDefault();
  });

  /* ------------------------------------------------- Rebuy recommendations */

  /* Rebuy's own recommendation widgets (the Smart Cart's "You may also like",
     widget 301655, and any other upsell widget) list the bundles like any product,
     and their Add button adds the row's product — the trigger shell. Rebuy tags
     each row with the product's tags, so a "Bundle" tagged row is `.tag-bundle`.

     Its Add is taken over here, in the capture phase on window, so Rebuy's own
     handler on the button never runs, and routed through add() above: the same
     components-only path as the product cards, with the same fall-through to the
     bundle's PDP. The Rebuy add is never let through, even when the takeover
     cannot start — that would be the shell. */
  var REC_BUNDLE_ADD = '.rebuy-product-block.tag-bundle .rebuy-product-actions .rebuy-button';
  /* Never these: the dynamic bundle widget itself (on the bundle PDP and in the
     off-screen host add() mounts), whose add is the one doing the real work. */
  var REC_EXCLUDE = '.widget-type-dynamic-bundle, .bundle-atc__host';

  window.addEventListener(
    'click',
    function (event) {
      if (event.button !== 0) return;
      var target = event.target;
      if (!target || !target.closest) return;
      var button = target.closest(REC_BUNDLE_ADD);
      if (!button || button.closest(REC_EXCLUDE)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var link = button.closest('.rebuy-product-block').querySelector('a[href*="/products/"]');
      var url = link ? new URL(link.getAttribute('href'), window.location.origin).pathname : '';
      log('taking over a Rebuy recommendation add', url || '(no product link)');
      if (!url) return;
      button.setAttribute('data-bundle-url', url);
      add(button);
    },
    true
  );

  window.bundleQuickShop = { add: add };
})();
